import React, { useEffect, useState } from "react";
import { X, Mail, Send, Plus, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * iter71 · Send Vendor Packet dialog.
 *
 * Renders on the order detail page when staff click "Send Vendor Packet".
 * Users can:
 *   1. Pick an existing club-added contact from the dropdown, OR
 *   2. Add a brand-new contact inline (mini form)
 *   3. Preview the subject/body summary (intro vs standard)
 *   4. Append order-specific notes
 *   5. Send. The server picks the template based on
 *      contact.introduction_email_sent.
 */
export default function SendVendorPacketDialog({ open, onClose, orderId, orderDisplayId, onSent }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({
    vendor_company_name: "", representative_name: "", representative_email: "",
    representative_title: "", representative_phone: "", brand: "",
    category: "", club_account_number: "", notes: "",
  });
  const [addBusy, setAddBusy] = useState(false);
  const [dupExisting, setDupExisting] = useState(null);
  // iter72 · Optional Secure Vendor Link — when checked, the send-packet
  // endpoint mints a fresh /v/{token} URL and embeds it in the email so
  // the vendor can respond without an account.
  const [includeSecureLink, setIncludeSecureLink] = useState(true);
  const [lastSecureLinkUrl, setLastSecureLinkUrl] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/club/vendor-contacts");
      setContacts(data.contacts || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setErr(""); setFlash(null); setSelectedId(""); setNotes("");
      setShowAdd(false); setDupExisting(null);
      setLastSecureLinkUrl(null);
      setNewForm({
        vendor_company_name: "", representative_name: "", representative_email: "",
        representative_title: "", representative_phone: "", brand: "",
        category: "", club_account_number: "", notes: "",
      });
      refresh();
    }
  }, [open]);

  if (!open) return null;

  const selected = contacts.find((c) => c.contact_id === selectedId);
  const willUseIntro = selected && !selected.introduction_email_sent;

  const submitNewContact = async (adopt = false) => {
    setAddBusy(true); setErr(""); setDupExisting(null);
    try {
      const url = adopt ? "/club/vendor-contacts?force=1" : "/club/vendor-contacts";
      const { data } = await api.post(url, newForm);
      const created = data.contact;
      // Merge into local list + auto-select
      setContacts((cs) => {
        const others = cs.filter((c) => c.contact_id !== created.contact_id);
        return [created, ...others];
      });
      setSelectedId(created.contact_id);
      setShowAdd(false);
      setFlash(data.reused ? "Reusing existing contact." : "Contact saved.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.existing_contact_id) {
        setDupExisting(detail);
      } else {
        setErr(formatApiErrorDetail(detail) || e.message);
      }
    } finally { setAddBusy(false); }
  };

  const sendPacket = async () => {
    if (!selectedId) { setErr("Please pick or add a vendor contact."); return; }
    setSending(true); setErr("");
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/send-vendor-packet`, {
        contact_id: selectedId,
        order_notes: notes.trim() || null,
        include_secure_link: includeSecureLink,
      });
      setLastSecureLinkUrl(data.secure_link_url || null);
      setFlash(
        data.template === "intro"
          ? "Introduction email + Vendor Packet sent."
          : "Vendor Packet sent."
      );
      onSent?.(data);
      // Refresh contact list so the intro-sent flag is reflected next time
      refresh();
      // If we minted a secure link, keep the dialog open a beat longer so
      // staff can copy the URL. Otherwise close briskly.
      setTimeout(() => { onClose?.(); }, data.secure_link_url ? 2600 : 900);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end"
         onClick={onClose}
         data-testid="send-vendor-packet-dialog">
      <div className="w-full max-w-xl h-full bg-cream shadow-2xl overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E5E3D8] flex items-start justify-between gap-3">
          <div>
            <div className="overline text-[#7A7A7A]">Send Vendor Packet</div>
            <div className="font-serif text-2xl mt-1 text-[#1A422D]">
              {orderDisplayId || orderId}
            </div>
            <p className="text-xs text-[#4A4A4A] mt-1">
              Sends the PDF packet to a vendor contact by email. First send to a new contact includes the introduction.
            </p>
          </div>
          <button onClick={onClose} data-testid="svp-close" className="text-[#4A4A4A] hover:text-[#1A1A1A]">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {err && (
            <div className="border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-sm flex items-start gap-2"
                 data-testid="svp-error">
              <AlertTriangle size={14} className="mt-0.5" />{err}
            </div>
          )}
          {flash && (
            <div className="border border-[#1A422D] bg-[#EFF3EC] text-[#1A422D] px-3 py-2 text-sm flex items-start gap-2"
                 data-testid="svp-flash">
              <CheckCircle2 size={14} className="mt-0.5" />{flash}
            </div>
          )}

          {/* Recipient */}
          <div>
            <label className="label-elegant block mb-1.5">Recipient</label>
            <div className="flex items-center gap-2">
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={loading}
                data-testid="svp-contact-select"
                className="input-field flex-1"
              >
                <option value="">
                  {loading ? "Loading contacts…" : contacts.length ? "Choose a vendor contact…" : "No saved contacts yet"}
                </option>
                {contacts.map((c) => (
                  <option key={c.contact_id} value={c.contact_id}>
                    {c.vendor_company_name} — {c.representative_name} · {c.representative_email}
                    {c.introduction_email_sent ? "" : " (new)"}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowAdd((v) => !v)}
                data-testid="svp-toggle-add"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-[0.22em] border border-[#1A422D] text-[#1A422D] hover:bg-[#1A422D] hover:text-[#F9F8F4]"
              >
                <Plus size={13} /> Add
              </button>
            </div>
          </div>

          {/* Add-new mini form */}
          {showAdd && (
            <div className="p-4 bg-white border border-[#E5E3D8]" data-testid="svp-add-form">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">
                New Vendor Contact
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Vendor Company *" testId="svp-add-company"
                       value={newForm.vendor_company_name}
                       onChange={(v) => setNewForm({ ...newForm, vendor_company_name: v })} />
                <Field label="Rep Name *" testId="svp-add-name"
                       value={newForm.representative_name}
                       onChange={(v) => setNewForm({ ...newForm, representative_name: v })} />
                <Field label="Rep Email *" type="email" testId="svp-add-email"
                       value={newForm.representative_email}
                       onChange={(v) => setNewForm({ ...newForm, representative_email: v })} />
                <Field label="Title" testId="svp-add-title"
                       value={newForm.representative_title}
                       onChange={(v) => setNewForm({ ...newForm, representative_title: v })} />
                <Field label="Phone" testId="svp-add-phone"
                       value={newForm.representative_phone}
                       onChange={(v) => setNewForm({ ...newForm, representative_phone: v })} />
                <Field label="Brand" testId="svp-add-brand"
                       value={newForm.brand}
                       onChange={(v) => setNewForm({ ...newForm, brand: v })} />
                <Field label="Category" testId="svp-add-category"
                       value={newForm.category}
                       onChange={(v) => setNewForm({ ...newForm, category: v })} />
                <Field label="Club Account #" testId="svp-add-account"
                       value={newForm.club_account_number}
                       onChange={(v) => setNewForm({ ...newForm, club_account_number: v })} />
              </div>

              {dupExisting && (
                <div className="mt-3 border border-[#B59A5A] bg-[#FBF6E8] p-3 text-sm text-[#8a6f33]"
                     data-testid="svp-dup-warning">
                  A vendor representative with this email already exists for your club
                  ({dupExisting.existing_vendor_company_name} — {dupExisting.existing_representative_name}).
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedId(dupExisting.existing_contact_id);
                        setShowAdd(false); setDupExisting(null);
                      }}
                      className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 bg-[#1A422D] text-[#F9F8F4]"
                      data-testid="svp-dup-use-existing"
                    >Use existing</button>
                    <button
                      onClick={() => submitNewContact(true)}
                      className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 border border-[#1A422D] text-[#1A422D]"
                      data-testid="svp-dup-adopt"
                    >Adopt & continue</button>
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={() => submitNewContact(false)}
                  disabled={addBusy || !newForm.vendor_company_name || !newForm.representative_name || !newForm.representative_email}
                  data-testid="svp-add-save"
                  className="bg-[#1A422D] text-[#F9F8F4] px-4 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#0f1a14] disabled:opacity-50"
                >
                  {addBusy ? "Saving…" : "Save Contact"}
                </button>
                <button onClick={() => { setShowAdd(false); setDupExisting(null); }}
                        className="text-xs uppercase tracking-[0.22em] text-[#4A4A4A]">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Preview */}
          {selected && (
            <div className="border border-[#E5E3D8] bg-white p-4" data-testid="svp-preview">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">
                Email Preview
              </div>
              <div className="mt-2 space-y-1 text-sm">
                <div><span className="text-[#7A7A7A] w-20 inline-block">To:</span> {selected.representative_name} &lt;{selected.representative_email}&gt;</div>
                <div><span className="text-[#7A7A7A] w-20 inline-block">Subject:</span>{" "}
                  {willUseIntro
                    ? "New Special-Order Process from your club"
                    : `Special-Order Request from your club — ${orderDisplayId}`}
                </div>
                <div><span className="text-[#7A7A7A] w-20 inline-block">Attach:</span> vendor-packet-{orderDisplayId || orderId}.pdf</div>
                <div className="mt-2">
                  <span className={willUseIntro
                    ? "inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] bg-[#FBF6E8] border border-[#B59A5A] text-[#8a6f33]"
                    : "inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] bg-[#EFF3EC] border border-[#1A422D] text-[#1A422D]"}
                        data-testid="svp-template-badge">
                    {willUseIntro ? "First-time introduction template" : "Standard follow-up template"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="label-elegant block mb-1.5" htmlFor="svp-notes">Order-specific notes (optional)</label>
            <textarea
              id="svp-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="svp-notes"
              className="input-field w-full text-sm"
              placeholder="e.g. Please rush by Friday · Ship to attention of the golf shop"
            />
          </div>

          {/* iter72 · Secure link toggle */}
          <label className="flex items-start gap-3 border border-[#B59A5A] bg-[#FBF6E8] p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeSecureLink}
              onChange={(e) => setIncludeSecureLink(e.target.checked)}
              data-testid="svp-include-secure-link"
              className="mt-0.5 h-4 w-4 accent-[#1A422D]"
            />
            <span className="text-xs text-[#4A4A4A] leading-relaxed">
              <span className="font-serif text-sm text-[#1A422D]">Include Secure Vendor Link</span><br />
              Embed a private, single-order response link in the email so the vendor can
              confirm quantities and upload documents without an account. Expires in 30 days.
            </span>
          </label>

          {lastSecureLinkUrl && (
            <div className="border border-[#1A422D] bg-[#EFF3EC] p-3 text-xs" data-testid="svp-secure-link-preview">
              <div className="uppercase tracking-[0.22em] text-[#1A422D] mb-1">Secure link (for your reference)</div>
              <div className="flex items-center gap-2">
                <code className="text-[11px] text-[#1A422D] break-all flex-1">{lastSecureLinkUrl}</code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(lastSecureLinkUrl)}
                  data-testid="svp-secure-link-copy"
                  className="shrink-0 border border-[#1A422D] text-[#1A422D] px-2 py-1 uppercase tracking-[0.2em] text-[10px] hover:bg-[#1A422D] hover:text-[#F9F8F4]"
                >Copy</button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={sendPacket}
              disabled={sending || !selectedId}
              data-testid="svp-send"
              className="inline-flex items-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-5 py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-[#0f1a14] disabled:opacity-50"
            >
              <Send size={13} /> {sending ? "Sending…" : "Send Now"}
            </button>
            <button onClick={onClose}
                    className="text-xs uppercase tracking-[0.22em] text-[#4A4A4A]"
                    data-testid="svp-cancel">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="label-elegant block mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="input-field w-full"
      />
    </label>
  );
}
